import {IHomepageService} from "./homepage.service.interface";
import {ServiceBase} from "../ServiceBase";
import { action, observable, makeObservable } from "mobx";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {GAME_ORIENTATION_IDS} from "../user-game-orientation/user-game-orientation.service.interface";
import { Check } from "@sparkware/app-utils";
import {BannerComponentProps} from "../../components/banner/banner.component";
import {HeaderArenasStyle} from "../../themes/architecture/sections/theme-layout.interface";
import {OrbitAIFeatureAnalyticsCategory} from "../analytics/analytics.service.interface";
import {HomepageHeaderArenasMenuItemConfigReader} from "./homepage-header-arenas-menu-item-config-reader";
import {MenuItemKey} from "../configurations/config-data-sections/generated-config-const";
import {IMenuItemRenderer} from "../menu-items-common/renderer/menu-item.renderer.interface";
import {IMenuItemActionFactoryService} from "../menu-item-action-factory/menu-item-action-factory.service.interface";
import {GenericHomepageHeaderArenaMenuItemRenderer} from "./menu-items/generic-homepage-header-arena-menu-item.renderer";

export const NEW_ADDED_GAMES_LOCAL_STORAGE_KEY = "new_added_games";

enum ARENA_IDS {
    SLOTS = "slotsMenuItem",
    JACKPOTS = "jackpotsMenuItem",
    BLACKJACK = "blackjackMenuItem",
    ROULETTE = "rouletteMenuItem",
    CASINO_GAMES = "casinoMenuItem",
    LIVE_CASINO = "liveCasinoMenuItem",
    SCRATCH_CARDS = "scratchCardsMenuItem",
    EXCLUSIVE888 = "exclusive888arenaMenuItem",
    VIDEO_POKER = "videoPokerMenuItem",
    GAME_SHOWS = "gameShowsMenuItem",
    NEW_GAMES = "newGamesMenuItem"
}

const USER_GROUP_TO_HEADER_ARENAS_MAPPING = {
    [GAME_ORIENTATION_IDS.SLOTS]: [ARENA_IDS.SLOTS, ARENA_IDS.JACKPOTS, ARENA_IDS.NEW_GAMES, ARENA_IDS.EXCLUSIVE888, ARENA_IDS.LIVE_CASINO, ARENA_IDS.GAME_SHOWS],
    [GAME_ORIENTATION_IDS.BLACKJACK]: [ARENA_IDS.LIVE_CASINO, ARENA_IDS.BLACKJACK, ARENA_IDS.NEW_GAMES, ARENA_IDS.SLOTS, ARENA_IDS.CASINO_GAMES, ARENA_IDS.JACKPOTS],
    [GAME_ORIENTATION_IDS.ROULETTE]: [ARENA_IDS.LIVE_CASINO, ARENA_IDS.ROULETTE,  ARENA_IDS.NEW_GAMES, ARENA_IDS.SLOTS, ARENA_IDS.CASINO_GAMES, ARENA_IDS.JACKPOTS],
    [GAME_ORIENTATION_IDS.LIVE_BLACKJACK]: [ARENA_IDS.LIVE_CASINO, ARENA_IDS.BLACKJACK, ARENA_IDS.NEW_GAMES, ARENA_IDS.SLOTS, ARENA_IDS.CASINO_GAMES, ARENA_IDS.JACKPOTS],
    [GAME_ORIENTATION_IDS.LIVE_ROULETTE]: [ARENA_IDS.LIVE_CASINO, ARENA_IDS.ROULETTE, ARENA_IDS.NEW_GAMES, ARENA_IDS.SLOTS, ARENA_IDS.CASINO_GAMES, ARENA_IDS.JACKPOTS],
    [GAME_ORIENTATION_IDS.LIVE_CASINO]: [ARENA_IDS.LIVE_CASINO, ARENA_IDS.ROULETTE, ARENA_IDS.NEW_GAMES,  ARENA_IDS.SLOTS, ARENA_IDS.CASINO_GAMES, ARENA_IDS.JACKPOTS],
    [GAME_ORIENTATION_IDS.CASINO_SKILL]: [ARENA_IDS.LIVE_CASINO, ARENA_IDS.CASINO_GAMES, ARENA_IDS.NEW_GAMES, ARENA_IDS.SLOTS, ARENA_IDS.JACKPOTS, ARENA_IDS.VIDEO_POKER],
    [GAME_ORIENTATION_IDS.CASINO_SOFT]: [ARENA_IDS.SLOTS, ARENA_IDS.JACKPOTS, ARENA_IDS.NEW_GAMES, ARENA_IDS.GAME_SHOWS, ARENA_IDS.EXCLUSIVE888, ARENA_IDS.SCRATCH_CARDS],
    [GAME_ORIENTATION_IDS.CASINO_MIXT]: [ARENA_IDS.LIVE_CASINO, ARENA_IDS.SLOTS,ARENA_IDS.NEW_GAMES, ARENA_IDS.JACKPOTS, ARENA_IDS.EXCLUSIVE888, ARENA_IDS.CASINO_GAMES]
};

export class HomepageService extends ServiceBase implements IHomepageService {

    constructor(services: IServiceFactoryExtended, private readonly menuItemActionFactory: IMenuItemActionFactoryService) {
        super(services);

        makeObservable(this);

        this._menuItemConfigReader = new HomepageHeaderArenasMenuItemConfigReader(services);

        this._setHeaderArenas(this.services.configuration.data.homePage.headerArenasOrder);

        this.reaction(() => this.services.userGameOrientation.gameGroup,
            (group) => {
                if (group?.groupId) {
                    this._orderHeaderArenas(group.groupId)
                }
            },
            {
                fireImmediately: true
            });

        this.reaction(() => this.services.user.isAuthorized,
            (isAuthorized) => {
                if (isAuthorized) {
                    this._updateNewAddedGames();
                } else {
                    this._setNewAddedGames(0);
                }
            },
            {
                fireImmediately: true
            });
    }

    private readonly _menuItemConfigReader: HomepageHeaderArenasMenuItemConfigReader;

    @observable.ref
    private _newAddedGames: number = 0;

    get newAddedGames(): number {
        return this._newAddedGames;
    }

    @action.bound
    _setNewAddedGames(newGamesCount: number) {
        this._newAddedGames = newGamesCount;
    }

    @observable.ref
    headerArenas: MenuItemKey[] = [];

    @action.bound
    private _setHeaderArenas(arenas: MenuItemKey[]): void {
        this.headerArenas = arenas;
    }

    private _orderHeaderArenas(id: number): void {

        let arenasNames = USER_GROUP_TO_HEADER_ARENAS_MAPPING[id];
        if (!arenasNames) {
            this.services.logger.error(`User orientation id ${id} is missing from mapping.`);
            return;
        }

        this._sendUserOrientationArenasAnalytics(arenasNames);
        this._setHeaderArenas(arenasNames);
    }

    private _sendUserOrientationArenasAnalytics(arenas: MenuItemKey[]) {
        this.services.analytics.sendOrbitAIFeatureEvent(OrbitAIFeatureAnalyticsCategory.AIArenaIconsDisplayed, this.services.userGameOrientation.gameGroup.groupName, arenas.join(','));
    }


    private async _updateNewAddedGames() {

        const selfUpdatedByNewestGamesList = this.services.configuration.getSelfUpdatedByNewestGamesList();
        if (!selfUpdatedByNewestGamesList || !Check.isArray(selfUpdatedByNewestGamesList.gamesId)) {
            this._setNewAddedGames(0);
            return;
        }

        //take only in-layout games
        const gamesInfo = await this.services.games.getGamesInfoArray(selfUpdatedByNewestGamesList.gamesId);
        const seenGames = this._getNewAddedGamesFromLocalStorage();

        const newGames: {[gameType: number] : number} = {};
        gamesInfo.reduce((newGames, gi) => {
            const gt = gi.gameType;
            if (gt && !seenGames[gt]) {
                newGames[gt] = gt;
            }
            return newGames;
        }, newGames);

        this._setNewAddedGames(Object.keys(newGames).length);
    }

    resetNewAddedGames() {
        if (this.services.user.isAuthorized) {
            this._resetNewAddedGames();
        }
    }

    private _resetNewAddedGames() {

        const selfUpdatedByNewestGamesList = this.services.configuration.getSelfUpdatedByNewestGamesList();
        if (!selfUpdatedByNewestGamesList || !Check.isArray(selfUpdatedByNewestGamesList.gamesId)) {
            return;
        }

        this._setNewAddedGamesFromLocalStorage(selfUpdatedByNewestGamesList.gamesId);
        this._setNewAddedGames(0);
    }

    private _getNewAddedGamesFromLocalStorage() : {[gameType: number] : number} {
        const result: {[gameType: number] : number} = {};
        try {
            const storedValue = this.services.localStorage.getItem(this._getNewAddedGamesLocalStorageKey()) as string;
            if (!Check.isString(storedValue)) {
                return result;
            }
            storedValue.split(';').forEach(gameType => {
                result[gameType] = gameType;
            });
            return result;
        } catch(err) {
            this.services.logger.error(`Failed to parse ${this._getNewAddedGamesLocalStorageKey()} from local storage! Error details: `, err);
            return result;
        }
    }

    private _setNewAddedGamesFromLocalStorage(newGames: number[]) {
        const gamesList = newGames.join(';')
        this.services.localStorage.setItem(this._getNewAddedGamesLocalStorageKey(), gamesList);
    }

    private _getNewAddedGamesLocalStorageKey(): string {
        return `${NEW_ADDED_GAMES_LOCAL_STORAGE_KEY}_${this.services.user.cid}`;
    }

    // mini top banner means a smaller banner + arena icons which not overlap with banner content (and also smaller on PC)
    get useMiniTopBanner(): boolean {
        return !Check.isEmpty(this.services.configuration.data.homePage.topBanner.miniTopBannerComponentID);
    }

    get useAdminBanner(): boolean {
        const bannerAdminIDFromConfig = this.services.configuration.data.homePage.topBanner.bannerAdminPlaceholderId;
        return !Check.isEmpty(bannerAdminIDFromConfig);
    }

    get headerArenasStyle() {
        const preferredStyle = this.services.theme.currentTheme.layout.headerArenasStyle;
        // if the preferred style is Menu (for MrGreen) or SmallTiles (this should never come from here) we return it directly,
        // but if it is null (the default) or BigTiles (also should never come from here) we have to check if we should use SmallTiles (from AB testing or something)
        // or we should leave the default BigTiles
        if (preferredStyle === HeaderArenasStyle.Menu || preferredStyle === HeaderArenasStyle.SmallTiles) {
            return preferredStyle;
        }
        if (this.useMiniTopBanner || this.useAdminBanner) {
            return HeaderArenasStyle.SmallTiles;
        } else {
            return HeaderArenasStyle.BigTiles;
        }
    }

    get headerArenasStyleIsBigTiles() {
        return this.headerArenasStyle === HeaderArenasStyle.BigTiles;
    }

    get headerArenasStyleIsSmallTiles() {
        return this.headerArenasStyle === HeaderArenasStyle.SmallTiles;
    }

    get headerArenasStyleIsMenu() {
        return this.headerArenasStyle === HeaderArenasStyle.Menu;
    }

    private _getMiniBannerComponentID() {
        return this.services.configuration.data.homePage.topBanner.miniTopBannerComponentID;
    }

    getTopBannerProps(): BannerComponentProps {
        const topBannerConfig = this.services.configuration.data.homePage.topBanner;
        const minHeight = this.services.theme.themeCustomizer.getHomePageTopBannerHeight();
        const componentID = this.useMiniTopBanner ? this._getMiniBannerComponentID() : topBannerConfig.componentID;
        return {
            showPlaceholder: true,
            minHeight: minHeight,
            componentID: componentID,
            templateID: topBannerConfig.templateID,
            fallbackComponent: topBannerConfig.fallbackComponent,
            bannerAdminPlaceholderId: topBannerConfig.bannerAdminPlaceholderId
        }
    }

    private _getMenuItem(key: MenuItemKey): IMenuItemRenderer {
        let customMenuItem = this.services.regulation.current.menuItems.getHomepageHeaderArenaMenuItem(key, this._menuItemConfigReader, this.menuItemActionFactory);
        if(customMenuItem) {
            return customMenuItem;
        }

        return new GenericHomepageHeaderArenaMenuItemRenderer(key, this._menuItemConfigReader, this.services, this.menuItemActionFactory);
    }

    getHeaderArenasMenuItems(): IMenuItemRenderer[] {
        return this.headerArenas.map(key => this._getMenuItem(key))
            .filter(menuItem => menuItem.display);
    }
}
